if (!customElements.get('filtered-featured-collection')) {
  customElements.define(
    'filtered-featured-collection',
    class FilteredFeaturedCollection extends HTMLElement {
      connectedCallback() {
        this.selectors = {
          sliderWrapper: '.featured-collection__items',
          nextEl: '.swiper-button-next',
          prevEl: '.swiper-button-prev',
          pagination: '.swiper-pagination',
        };
        this.classes = {
          grid: 'f-grid',
          swiper: 'swiper',
          swiperWrapper: 'swiper-wrapper',
        };

        this.blockId = this.dataset.blockId;
        this.block = this.closest(`[data-id="${this.blockId}"]`);
        this.sliderWrapper = this.querySelector(this.selectors.sliderWrapper);
        this.enableSlider = this.dataset.enableSlider === 'true';
        this.items = parseInt(this.dataset.items, 10);
        this.tabletItems = parseInt(this.dataset.tabletItems, 10);
        this.laptopItems = parseInt(this.dataset.laptopItems, 10);
        this.paginationType = this.dataset.paginationType || 'bullets';
        this.productLimit = parseInt(this.dataset.productLimit, 10) || 8;
        this.filterQuery = (this.dataset.filterQuery || '').trim();
        this.sliderInstance = false;
        this.abortController = new AbortController();
        this.onMediaChange = this.init.bind(this);

        if (this.filterQuery && this.dataset.collectionUrl) {
          this.loadFilteredProducts();
        } else {
          this.finishSetup();
        }
      }

      disconnectedCallback() {
        this.abortController?.abort();
        this.mediaQuery?.removeEventListener?.('change', this.onMediaChange);
      }

      buildFilteredUrl() {
        const collectionUrl = new URL(this.dataset.collectionUrl, window.location.origin);
        let filterParams;

        try {
          const copiedUrl = new URL(this.filterQuery, window.location.origin);
          filterParams = copiedUrl.searchParams;
        } catch (error) {
          filterParams = new URLSearchParams(this.filterQuery.replace(/^\?/, ''));
        }

        // A plain query string is interpreted as a relative path by URL(), so parse it explicitly.
        if (!this.filterQuery.includes('?') && !this.filterQuery.startsWith('/') && !/^https?:\/\//i.test(this.filterQuery)) {
          filterParams = new URLSearchParams(this.filterQuery.replace(/^\?/, ''));
        }

        collectionUrl.search = '';
        filterParams.forEach((value, key) => {
          if (!['page', 'section_id', 'sections', 'view'].includes(key)) {
            collectionUrl.searchParams.append(key, value);
          }
        });
        collectionUrl.searchParams.set('section_id', 'product-grid');

        return collectionUrl;
      }

      async loadFilteredProducts() {
        this.setAttribute('aria-busy', 'true');
        this.sliderWrapper.style.opacity = '0';

        try {
          let url = this.buildFilteredUrl();
          let source;

          try {
            source = await this.fetchProductList(url);
          } catch (error) {
            if (error.name === 'AbortError') throw error;
          }

          // Fall back to the complete collection page when its template uses a
          // different section ID or Shopify rejects section rendering.
          if (!source) {
            url.searchParams.delete('section_id');
            source = await this.fetchProductList(url);
          }

          if (!source) throw new Error('The filtered collection product list was not found.');
          this.replaceProducts(source);
        } catch (error) {
          if (error.name !== 'AbortError') {
            console.error('Filtered featured collection:', error);
            this.dataset.filterError = 'true';
          }
        } finally {
          this.sliderWrapper.style.opacity = '';
          this.removeAttribute('aria-busy');
          this.finishSetup();
        }
      }

      async fetchProductList(url) {
        const response = await fetch(url.toString(), {
          headers: { 'X-Requested-With': 'XMLHttpRequest' },
          signal: this.abortController.signal,
        });

        if (!response.ok) throw new Error(`Collection request failed with status ${response.status}.`);

        const responseText = await response.text();
        const parsedHTML = new DOMParser().parseFromString(responseText, 'text/html');
        return parsedHTML.querySelector('#ProductsList');
      }

      replaceProducts(source) {
        const productColumns = Array.from(source.children)
          .filter((column) => column.querySelector('.product-card'))
          .slice(0, this.productLimit);

        Array.from(this.sliderWrapper.children).forEach((column) => {
          if (column.querySelector('.product-card, .placeholder-svg')) column.remove();
        });

        if (productColumns.length === 0) {
          const emptyColumn = document.createElement('div');
          emptyColumn.className = 'f-column';
          emptyColumn.dataset.filteredProduct = 'true';
          const message = document.createElement('p');
          message.className = 'text-subtext';
          message.textContent = this.dataset.emptyMessage || 'No products match the selected filters.';
          emptyColumn.appendChild(message);
          this.sliderWrapper.appendChild(emptyColumn);
          return;
        }

        productColumns.forEach((sourceColumn) => {
          const column = sourceColumn.cloneNode(true);
          column.className = `f-column${this.enableSlider ? ' swiper-slide' : ''}`;
          column.dataset.filteredProduct = 'true';
          column.querySelectorAll('compare-checkbox').forEach((checkbox) => checkbox.remove());
          this.applyImageRatio(column);
          this.sliderWrapper.appendChild(column);
        });
      }

      applyImageRatio(column) {
        const imageRatio = this.dataset.imageRatio;
        if (!imageRatio) return;

        column.querySelectorAll('[style*="--aspect-ratio"]').forEach((media) => {
          let ratio = imageRatio;
          if (ratio === 'adapt') {
            const image = media.querySelector('img[width][height]');
            if (!image) return;
            ratio = `${image.getAttribute('width')}/${image.getAttribute('height')}`;
          }
          media.style.setProperty('--aspect-ratio', ratio);
        });
      }

      finishSetup() {
        const productCount = this.sliderWrapper.querySelectorAll(':scope > .f-column').length;

        if (this.enableSlider && productCount <= this.items) {
          this.enableSlider = false;
          this.classList.remove(this.classes.swiper);
          this.sliderWrapper.classList.remove(this.classes.swiperWrapper);
          this.sliderWrapper.classList.add(this.classes.grid);
          const controls = this.block?.querySelector('.swiper-controls');
          if (controls) controls.style.display = 'none';
        }

        if (!this.enableSlider) return;

        this.mediaQuery = window.matchMedia(FoxTheme.config.mediaQueryMobile);
        this.mediaQuery.addEventListener?.('change', this.onMediaChange);
        this.init();
      }

      init() {
        if (FoxTheme.config.mqlMobile) {
          this.destroySlider();
        } else {
          this.initSlider();
        }
      }

      initSlider() {
        if (typeof this.sliderInstance === 'object') return;

        const columnGap = window.getComputedStyle(this.sliderWrapper).getPropertyValue('--f-column-gap');
        const spaceBetween = parseFloat(columnGap.replace('rem', '')) * 10;
        this.sliderOptions = {
          slidesPerView: 2,
          spaceBetween,
          navigation: {
            nextEl: this.block?.querySelector(this.selectors.nextEl),
            prevEl: this.block?.querySelector(this.selectors.prevEl),
          },
          pagination: {
            el: this.block?.querySelector(this.selectors.pagination),
            type: this.paginationType,
          },
          breakpoints: {
            768: { slidesPerView: this.tabletItems },
            1024: { slidesPerView: this.laptopItems },
            1280: { slidesPerView: this.items },
          },
          loop: false,
          threshold: 2,
          watchSlidesProgress: true,
          mousewheel: { enabled: true, forceToAxis: true },
        };

        this.classList.add(this.classes.swiper);
        this.sliderWrapper.classList.remove(this.classes.grid);
        this.sliderWrapper.classList.add(this.classes.swiperWrapper);
        this.sliderInstance = new window.FoxTheme.Carousel(this, this.sliderOptions, [FoxTheme.Swiper.Mousewheel]);
        this.sliderInstance.init();
        this.calcNavButtonsPosition();
        this.fixQuickviewDuplicate();
      }

      destroySlider() {
        this.classList.remove(this.classes.swiper);
        this.sliderWrapper.classList.remove(this.classes.swiperWrapper);
        this.sliderWrapper.classList.add(this.classes.grid);
        if (typeof this.sliderInstance !== 'object') return;
        this.sliderInstance.slider.destroy();
        this.sliderInstance = false;
      }

      fixQuickviewDuplicate() {
        const modalIds = [];
        this.querySelectorAll('quick-view-modal').forEach((modal) => {
          if (modalIds.includes(modal.id)) modal.remove();
          else modalIds.push(modal.id);
        });
      }

      calcNavButtonsPosition() {
        if (this.dataset.navigationPosition !== 'center_image') return;
        const firstMedia = this.querySelector('.product-card__image-wrapper');
        const controls = this.block?.querySelector('.swiper-controls');
        if (controls && firstMedia && firstMedia.clientHeight > 0) {
          controls.style.setProperty('--swiper-navigation-top-offset', `${parseInt(firstMedia.clientHeight, 10) / 2}px`);
        }
      }
    }
  );
}
